import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';
import { useRef, useState } from 'react';

const IndustriesServed = () => {
  const [ref, isInView] = useInView({ threshold: 0.2 });
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const industries = [
    {
      name: 'Real Estate Development',
      description: 'Multi-residential, mixed-use, and commercial developments',
      scale: '$1M+ Projects',
      icon: 'ri-building-line',
      image: 'https://public.readdy.ai/ai/img_res/56b3bbc90f8d663f5a1fe6afd538ef4a.jpg',
    },
    {
      name: 'General Contracting',
      description: 'Full-service construction and project management',
      scale: '$500K+ Projects',
      icon: 'ri-hammer-line',
      image: 'https://readdy.ai/api/search-image?query=professional%20construction%20site%20with%20general%20contractor%20managing%20large%20scale%20building%20project%20with%20workers%20and%20equipment%20in%20organized%20clean%20setting&width=500&height=400&seq=ind-contractor-001&orientation=portrait',
    },
    {
      name: 'Hospitality',
      description: 'Hotels, resorts, and hospitality facilities',
      scale: '$2M+ Projects',
      icon: 'ri-hotel-line',
      image: 'https://readdy.ai/api/search-image?query=luxury%20hotel%20lobby%20interior%20with%20modern%20elegant%20design%20featuring%20premium%20finishes%20and%20sophisticated%20architectural%20details%20in%20bright%20welcoming%20atmosphere&width=500&height=400&seq=ind-hospitality-001&orientation=portrait',
    },
    {
      name: 'Industrial',
      description: 'Warehouses, manufacturing facilities, and logistics centers',
      scale: '$3M+ Projects',
      icon: 'ri-factory-line',
      image: 'https://readdy.ai/api/search-image?query=modern%20industrial%20warehouse%20facility%20with%20steel%20structure%20and%20organized%20layout%20showing%20professional%20construction%20and%20clean%20efficient%20design&width=500&height=400&seq=ind-industrial-001&orientation=portrait',
    },
    {
      name: 'Mixed-Use',
      description: 'Integrated residential, commercial, and retail spaces',
      scale: '$5M+ Projects',
      icon: 'ri-community-line',
      image: 'https://readdy.ai/api/search-image?query=contemporary%20mixed%20use%20development%20building%20with%20retail%20ground%20floor%20and%20residential%20upper%20levels%20in%20modern%20urban%20architecture%20with%20clean%20design&width=500&height=400&seq=ind-mixeduse-001&orientation=portrait',
    },
    {
      name: 'Infrastructure',
      description: 'Public works, utilities, and civil engineering projects',
      scale: '$10M+ Projects',
      icon: 'ri-road-map-line',
      image: 'https://readdy.ai/api/search-image?query=modern%20infrastructure%20construction%20project%20with%20bridge%20or%20highway%20development%20showing%20professional%20engineering%20and%20organized%20construction%20site&width=500&height=400&seq=ind-infrastructure-001&orientation=portrait',
    },
  ];

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="text-xs font-semibold text-primary-600 tracking-widest uppercase mb-4">
            INDUSTRIES WE SERVE
          </div>
          <h2 className="font-display font-light text-4xl md:text-5xl text-gray-900 mb-4">
            Trusted Across <span className="font-semibold">Multiple Sectors</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            From residential developments to large-scale infrastructure, we deliver for projects of all sizes
          </p>
        </motion.div>

        <div
          ref={scrollRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide cursor-grab active:cursor-grabbing"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {industries.map((industry, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="flex-shrink-0 w-80 bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={industry.image}
                  alt={industry.name}
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-lg">
                    <i className={`${industry.icon} text-primary-600 text-2xl`}></i>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-display font-semibold text-xl text-gray-900 mb-2">
                  {industry.name}
                </h3>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  {industry.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                    {industry.scale}
                  </span>
                  <i className="ri-arrow-right-line text-gray-400"></i>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustriesServed;